import type { QueryClient } from '@tanstack/react-query';
import { knowledgeService } from '../../../services/KnowledgeService';
import type {
  KnowledgeBaseDocument,
  WithResourceId,
} from '../../../types/knowledge';
import { toast } from '../../../toast/toast';
import type { I18nContextValue } from '../../../types/i18nContext';
import type { OperationProgress } from '../../../utils/operationProgress';
import {
  allSelectedSupport,
  canRetryFailedUploadDocument,
  getDocumentLabel,
} from '../documentActionEligibility';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';

type Translate = I18nContextValue['translate'];

const MAX_UPLOAD_ATTEMPTS = 3;
const UPLOAD_RETRY_DELAY_MS = 500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function buildFileFromDocument(
  document: WithResourceId<KnowledgeBaseDocument>,
  blob: Blob,
): File {
  const fileName =
    document.fileName?.trim() || document.displayName?.trim() || 'document';
  return new File([blob], fileName, {
    type: document.mimeType?.trim() || blob.type || 'application/octet-stream',
  });
}

function triggerBrowserDownload(file: File): void {
  const objectUrl = URL.createObjectURL(file);
  const anchor = window.document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = file.name;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

/** Fetches bytes, saves via browser download, and returns a File for reuse. */
export async function downloadDocumentFileForRetry(
  databaseId: string,
  document: WithResourceId<KnowledgeBaseDocument>,
): Promise<File> {
  const blob = await knowledgeService.downloadDocument(databaseId, document.id);
  const file = buildFileFromDocument(document, blob);
  triggerBrowserDownload(file);
  return file;
}

async function uploadDocumentWithRetries(
  databaseId: string,
  file: File,
): Promise<void> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
    try {
      await knowledgeService.uploadDocument(databaseId, file);
      return;
    } catch (error) {
      lastError = error;
      if (attempt < MAX_UPLOAD_ATTEMPTS) {
        await delay(UPLOAD_RETRY_DELAY_MS);
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Document upload failed');
}

async function resolveFileForRetry(options: {
  databaseId: string;
  document: WithResourceId<KnowledgeBaseDocument>;
  preparedFiles: ReadonlyMap<string, File>;
}): Promise<File> {
  const prepared = options.preparedFiles.get(options.document.id);
  if (prepared != null) {
    return prepared;
  }

  const blob = await knowledgeService.downloadDocument(
    options.databaseId,
    options.document.id,
  );
  return buildFileFromDocument(options.document, blob);
}

type DownloadRetryDocumentsOptions = {
  databaseId: string;
  targets: Array<WithResourceId<KnowledgeBaseDocument>>;
  preparedFiles: ReadonlyMap<string, File>;
  translate: Translate;
  onProgress: (progress: OperationProgress) => void;
};

export async function downloadRetryDocuments(
  options: DownloadRetryDocumentsOptions,
): Promise<{
  preparedFiles: Map<string, File>;
  allSucceeded: boolean;
}> {
  const nextPrepared = new Map(options.preparedFiles);
  const pending = options.targets.filter(
    (document) => !nextPrepared.has(document.id),
  );
  const total = pending.length;

  options.onProgress({ completed: 0, total });

  let completed = 0;
  let allSucceeded = true;

  for (const document of pending) {
    try {
      const file = await downloadDocumentFileForRetry(
        options.databaseId,
        document,
      );
      nextPrepared.set(document.id, file);
    } catch {
      allSucceeded = false;
      toast.error({
        title: options.translate('knowledge.downloadErrorTitle'),
        description: options.translate('knowledge.downloadErrorDescription'),
      });
    }
    completed += 1;
    options.onProgress({ completed, total });
  }

  return { preparedFiles: nextPrepared, allSucceeded };
}

type RetryDocumentsOptions = {
  databaseId: string;
  targets: Array<WithResourceId<KnowledgeBaseDocument>>;
  preparedFiles: ReadonlyMap<string, File>;
  queryClient: QueryClient;
  translate: Translate;
  clearSelection: () => void;
  setIsBulkActionRunning: (value: boolean) => void;
  setBulkConfirm: (value: null) => void;
  onProgress?: (progress: OperationProgress) => void;
};

export async function retryDocuments({
  databaseId,
  targets,
  preparedFiles,
  queryClient,
  translate,
  clearSelection,
  setIsBulkActionRunning,
  setBulkConfirm,
  onProgress,
}: RetryDocumentsOptions): Promise<void> {
  if (
    targets.length === 0 ||
    !allSelectedSupport(targets, canRetryFailedUploadDocument)
  ) {
    return;
  }

  setIsBulkActionRunning(true);
  onProgress?.({ completed: 0, total: targets.length });
  let successCount = 0;

  for (let index = 0; index < targets.length; index += 1) {
    const document = targets[index];
    if (document === undefined) {
      continue;
    }

    const label = getDocumentLabel(document);
    try {
      const file = await resolveFileForRetry({
        databaseId,
        document,
        preparedFiles,
      });
      await knowledgeService.deleteDocument(databaseId, document.id);
      await uploadDocumentWithRetries(databaseId, file);
      successCount += 1;
    } catch {
      toast.error({
        title: translate('knowledge.documentRetryErrorTitle'),
        description: translate('knowledge.documentRetryErrorDescription', {
          fileName: label,
        }),
      });
    }
    onProgress?.({ completed: index + 1, total: targets.length });
  }

  void queryClient.invalidateQueries({
    queryKey: knowledgeQueryKeys.documentsRoot(databaseId),
  });
  invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
  setIsBulkActionRunning(false);
  setBulkConfirm(null);
  clearSelection();

  if (successCount === 1) {
    toast.success({
      title: translate('knowledge.documentRetrySuccessTitle'),
      description: translate('knowledge.documentRetrySuccessDescription'),
    });
  } else if (successCount > 1) {
    toast.success({
      title: translate('knowledge.documentRetryBatchSuccessTitle'),
      description: translate('knowledge.documentRetryBatchSuccessDescription', {
        count: String(successCount),
      }),
    });
  }
}
